import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import { alpha, Autocomplete, Box, Grid, Modal, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import RichTextField from 'components/ContractDialog/MuiRichTextField'
import CancelButton from 'components/ui/cancel-button'
import GeneralSaveButton from 'components/ui/general-save-button'
import {
  digitalOptimizations,
  mtbInsights,
  mtbLeasingPercentages,
  mtbObservations,
  optimizations,
} from 'config/optimizations'
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Optimization, OptimizationDetails } from 'types'
import FormTypeSelect from '../Forms/FormTypeSelect'
import DynamicSection from './DynamicSection'
import { formatTooltipContent } from './formatTooltipContent'

interface OptimizationsModalProps {
  open: boolean
  onSave: (data: OptimizationDetails & { type: string }) => void
  onClose: () => void
  organizationName: string
  selectedType: string
  setSelectedType: (type: string) => void
  context: string
  optimizationDetails: OptimizationDetails
  setOptimizationDetails: Dispatch<SetStateAction<OptimizationDetails>>
}

const OptimizationsModal: React.FC<OptimizationsModalProps> = ({
  open,
  onSave,
  onClose,
  organizationName,
  selectedType,
  setSelectedType,
  optimizationDetails,
  setOptimizationDetails,
  context,
}) => {
  const theme = useTheme()
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')
  const [localOpts, setLocalOpts] = useState('0')
  const [localNotes, setLocalNotes] = useState('<p></p>')
  const [localPercentage, setLocalPercentage] = useState('')
  const [isValid, setIsValid] = useState(false)

  // useEffect(() => {
  //   const runValidation = () => {
  //     const optimization = optimizationDetails?.optimization
  //     if (!optimization?.channel) return false
  //     if (!optimization?.name) return false
  //     if (!optimization?.type) return false
  //     if (selectedType === 'leasingPercentage') {
  //       return !!optimizationDetails.percentage
  //     }
  //     if (selectedType === 'insight') {
  //       return optimizationDetails.startDate && optimizationDetails.endDate
  //     } else {
  //       return !!optimizationDetails.followDate
  //     }
  //     return true
  //   }

  //   setIsValid(runValidation())
  // }, [optimizationDetails])
  useEffect(() => {
    const runValidation = () => {
      const optimization = optimizationDetails?.optimization
      if (!optimization?.channel || !optimization?.name || !optimization?.type) {
        return false
      }
      if (selectedType === 'leasingPercentage') return !!localPercentage.trim() || !!optimizationDetails.followDate
      if (selectedType === 'insight')
        return (!!optimizationDetails.startDate && !!optimizationDetails.endDate) || !!optimizationDetails.followDate

      // For observation, seo, digital, etc.
      return !!optimizationDetails.followDate
    }

    setIsValid(runValidation())
  }, [optimizationDetails, selectedType])

  useEffect(() => {
    setLocalOpts(String(optimizationDetails.opts))
  }, [optimizationDetails.opts])

  useEffect(() => {
    if (!open) {
      setOptimizationDetails({
        optimization: null,
        addtlNotes: '',
        opts: 0,
        percentage: 0,
        followDate: null,
        startDate: null,
        endDate: null,
      })
      setLocalNotes('<p></p>')
      setLocalPercentage('')
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    if (selectedType === 'insight' && mtbInsights.length > 0) {
      setOptimizationDetails({
        optimization: mtbInsights[0],
        addtlNotes: '',
        opts: 0,
        percentage: 0,
        followDate: null,
        owner: currentUser?.user_uuid,
        startDate: null,
        endDate: null,
      })
    }

    if (selectedType === 'observation' && mtbObservations.length > 0) {
      setOptimizationDetails({
        optimization: mtbObservations[0],
        addtlNotes: '',
        opts: 0,
        percentage: 0,
        followDate: null,
        owner: currentUser?.user_uuid,
      })
    }
    if (selectedType === 'leasingPercentage' && mtbLeasingPercentages.length > 0) {
      setOptimizationDetails({
        optimization: mtbLeasingPercentages[0],
        addtlNotes: '',
        opts: 0,
        percentage: 0,
        followDate: null,
        owner: currentUser?.user_uuid,
      })
      setLocalPercentage('')
    }
  }, [open, selectedType])

  const handleSelect = (_: any, value: Optimization | null) => {
    setOptimizationDetails({
      optimization: value,
      addtlNotes: '',
      opts: 0,
      percentage: 0,
      followDate: null,
      owner: currentUser?.user_uuid,
    })
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setOptimizationDetails(prev => ({ ...prev, followDate: value }))
  }

  const handleChangeNotes = (newValue: string) => {
    setLocalNotes(newValue)
    setOptimizationDetails(prev => ({ ...prev, addtlNotes: newValue }))
  }

  const handleTypeChange = (value: string) => {
    if (value !== selectedType) {
      setSelectedType(value)
      setOptimizationDetails({
        optimization: null,
        addtlNotes: '',
        opts: 0,
        percentage: 0,
        followDate: null,
        owner: currentUser?.user_uuid,
      })
    }
  }

  const handleChangeOpts = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
    if (value === '') return setLocalOpts('')
    if (!/^\d+$/.test(value)) return
    const numValue = Math.min(Math.max(parseInt(value, 10), 0), 10)
    setLocalOpts(String(numValue))
  }
  const handleChangeLeasingPercentage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalPercentage(e.target.value)
  }

  const handleDateRangeChange = (newStart: Date | null, newEnd: Date | null) => {
    setOptimizationDetails(prev => ({
      ...prev,
      startDate: newStart ? newStart.toISOString() : null,
      endDate: newEnd ? newEnd.toISOString() : null,
    }))
  }

  const handleBlurOpts = () => {
    const numValue = localOpts === '' ? 0 : parseInt(localOpts, 10)
    setLocalOpts(String(numValue))
    setOptimizationDetails(prev => ({
      ...prev,
      opts: numValue,
      optimization: prev.optimization ? { ...prev.optimization, opts: numValue } : null,
    }))
  }
  const handleBlurLeasingPercentage = () => {
    const parsed = parseFloat(localPercentage)
    const numValue = isNaN(parsed) ? 0 : Math.min(Math.max(parsed, 0), 100)

    setLocalPercentage(String(numValue))

    setOptimizationDetails(prev => ({
      ...prev,
      optimization: prev.optimization
        ? {
            ...prev.optimization,
            percentage: numValue,
          }
        : null,
    }))
  }

  const handleSave = () => {
    onSave({ ...optimizationDetails, type: selectedType })
  }
  const optSelectionTypes = [
    { label: 'SEO', value: 'seo' },
    { label: 'Digital', value: 'digital' },
  ]
  const modalTitle =
    selectedType === 'observation'
      ? 'Log Observation'
      : selectedType === 'insight'
      ? 'Log Insight'
      : selectedType === 'leasingPercentage'
      ? 'Log Leasing Percentage'
      : 'Log Optimizations'
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ height: '100%', maxHeight: '100vh', overflowY: 'auto' }}>
        <Grid
          container
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: theme.palette.background.default,
            padding: 4,
            width: '70%',
            maxWidth: 700,
            margin: 'auto',
            marginLeft: '10%',
            marginTop: '5%',
            borderRadius: 1,
            boxShadow: 24,
            spacing: 1,
          }}
        >
          <Typography variant="h5">{modalTitle}</Typography>
          <Grid item xs={12}>
            <TextField
              label="Organization"
              value={organizationName ?? ''}
              fullWidth
              margin="normal"
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { color: theme.palette.text.highlight } }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormTypeSelect
              key={`seo-select-type`}
              options={
                selectedType === 'observation'
                  ? [{ label: 'Observation', value: 'observation' }]
                  : selectedType === 'insight'
                  ? [{ label: 'Insight', value: 'insight' }]
                  : selectedType === 'leasingPercentage'
                  ? [{ label: 'Leasing Percentage', value: 'leasingPercentage' }]
                  : optSelectionTypes
              }
              value={selectedType}
              onChange={handleTypeChange}
            />
          </Grid>
          {selectedType && (
            <Grid item xs={12} pt={1}>
              <Autocomplete
                options={
                  selectedType === 'seo'
                    ? optimizations
                    : selectedType === 'observation'
                    ? mtbObservations
                    : selectedType === 'insight'
                    ? mtbInsights
                    : selectedType === 'leasingPercentage'
                    ? mtbLeasingPercentages
                    : digitalOptimizations
                }
                value={optimizationDetails.optimization || null} // ✅ Controlled value from state
                getOptionLabel={option => option?.name || ''}
                isOptionEqualToValue={(option, value) => option?.key === value?.key}
                onChange={handleSelect}
                sx={{ width: '100%' }}
                renderOption={(props, option) => {
                  const { key, ...rest } = props
                  return (
                    <li key={`${option.name}-${option.key}-${option.channel}-${option.module}`} {...rest}>
                      <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography variant="body1" sx={{ flex: 1, textAlign: 'left' }}>
                          {option.name}
                        </Typography>
                        <Typography variant="body2" sx={{ flex: 1, textAlign: 'center', color: option.color }}>
                          {option.channel}
                        </Typography>
                        {option.tooltip && (
                          <Box sx={{ flex: 1, textAlign: 'right' }}>
                            <Tooltip
                              leaveDelay={200}
                              title={
                                <Box
                                  sx={{
                                    width: '600px',
                                    maxWidth: '600px',
                                    minWidth: '500px',
                                    textAlign: 'left',
                                    whiteSpace: 'normal',
                                    padding: '12px',
                                    backgroundColor: alpha(theme.palette.background.default, 0.95),
                                    color: theme.palette.text.primary,
                                    borderRadius: '6px',
                                    boxShadow: `0px 4px 12px ${alpha(theme.palette.text.primary, 0.3)}`,
                                  }}
                                >
                                  {formatTooltipContent(option.tooltip)}
                                </Box>
                              }
                              placement="right"
                              arrow
                            >
                              <QuestionMarkIcon />
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </li>
                  )
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={
                      selectedType === 'observation'
                        ? 'Select Observation'
                        : selectedType === 'insight'
                        ? 'Select Insight'
                        : selectedType === 'leasingPercentage'
                        ? 'Select Leasing Percentage'
                        : 'Select Optimization'
                    }
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>
          )}
          {optimizationDetails.optimization && (
            <Grid container sx={{ display: 'flex', alignItems: 'center' }}>
              <DynamicSection
                key={'dynamic-section'}
                optimizationDetails={optimizationDetails}
                onDateChange={handleDateChange}
                onDateRangeChange={handleDateRangeChange}
                onChangeOpts={handleChangeOpts}
                onBlurOpts={handleBlurOpts}
                onChangePercentage={handleChangeLeasingPercentage}
                onBlurPercentage={handleBlurLeasingPercentage}
                localOpts={localOpts}
                localPercentage={localPercentage}
                selectedType={selectedType}
              />
              <Grid item xs={12}>
                <Typography sx={{ color: 'text.highlight', fontStyle: 'italic' }}>
                  {optimizationDetails.optimization?.description || ''}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <RichTextField
                  content={localNotes || '<p></p>'}
                  onContentChange={handleChangeNotes}
                  onBlur={() => {}}
                  minHeight={'100px'}
                />
              </Grid>
            </Grid>
          )}
          <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end', width: '100%', pt: 1, gap: 2 }}>
            <CancelButton onClick={onClose} />
            <GeneralSaveButton onClick={handleSave} disabled={!isValid} />
          </Box>
        </Grid>
      </Box>
    </Modal>
  )
}

export default OptimizationsModal
